import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { configureAmplify, isCognitoConfigured } from '@/lib/amplify'
import { hasOAuthCallbackParams, loginWithCognito } from '@/lib/auth'
import { useHomePath } from '@/pages/WorkspaceHome'
import { useAuthStore } from '@/store/auth'

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const home = useHomePath()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (isLoading || hasOAuthCallbackParams()) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-sidebar text-sm text-sidebar-foreground" role="status">
        Completing sign-in…
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={home} replace />
  }

  async function handleCognitoLogin() {
    setError(null)
    setIsRedirecting(true)
    try {
      configureAmplify()
      await loginWithCognito()
    } catch (err) {
      console.error('[auth] Cognito redirect failed', err)
      setError('Could not open the sign-in page. Try again, or ask the platform team if it keeps failing.')
      setIsRedirecting(false)
    }
  }

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-sidebar px-4 py-10 text-[15px] leading-[22px] text-sidebar-foreground">
      <main className="flex w-full max-w-[400px] flex-col gap-7">
        <div>
          <p className="text-[15px] font-semibold text-sidebar-label">EsaFX</p>
          <h1 className="mt-1 text-[30px] leading-9 font-bold text-white">Backoffice</h1>
          <p className="mt-2.5">Dealing, settlement and finance desk. Sign in with your staff account.</p>
        </div>
        <div className="flex flex-col gap-3 border-t border-sidebar-accent pt-6">
          <Button
            variant="secondary"
            className="h-12 w-full text-[15px] font-bold focus-visible:outline-sidebar-ring"
            disabled={isRedirecting || !isCognitoConfigured()}
            onClick={() => void handleCognitoLogin()}
          >
            {isRedirecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
                Opening the sign-in page
              </>
            ) : (
              'Sign in with staff account'
            )}
          </Button>
          {/* The button is disabled while unconfigured, so that message cannot wait for a click. */}
          {!isCognitoConfigured() || error ? (
            <p role="alert" className="rounded-md bg-[#3e211d] px-3 py-2.5 text-[#f2a196]">
              {isCognitoConfigured() ? error : 'Sign-in is not configured for this build. Ask the platform team to set the VITE_COGNITO_* variables.'}
            </p>
          ) : null}
        </div>
      </main>
      <p className="mt-12 text-[13px] text-sidebar-label">&copy; {new Date().getFullYear()} EsaFX</p>
    </div>
  )
}
